// Pure courier-escalation logic — no React/Firebase. Drives priority, ownership,
// workflow transitions and SLA timers for the Universal Courier Escalation Engine.
// Every shipping partner funnels into ONE escalation per order through this brain.

// ─── Canonical reasons (auto-trigger → reason) ───────────────────────────────
export const ESCALATION_REASONS = {
  PICKUP_NOT_DONE: 'pickup_not_done',
  PICKUP_DELAYED: 'pickup_delayed',
  PICKUP_CANCELLED: 'pickup_cancelled',
  MANIFEST_FAILURE: 'manifest_failure',
  SHIPMENT_DELAYED: 'shipment_delayed',
  OFD_EXCEEDED: 'ofd_exceeded',
  NDR: 'ndr',
  CUSTOMER_UNAVAILABLE: 'customer_unavailable',
  ADDRESS_INCOMPLETE: 'address_incomplete',
  WRONG_MOBILE: 'wrong_mobile',
  PINCODE_ISSUE: 'pincode_issue',
  LOST: 'lost',
  DAMAGED: 'damaged',
  FAKE_DELIVERY: 'fake_delivery',
  DELIVERED_NOT_RECEIVED: 'delivered_not_received',
  WRONG_DELIVERY: 'wrong_delivery',
  TAMPERED: 'tampered',
  PARCEL_MISSING: 'parcel_missing',
  PARTIAL_SHIPMENT: 'partial_shipment',
  RTO_INITIATED: 'rto_initiated',
  RTO_DELIVERED: 'rto_delivered',
  MULTIPLE_ATTEMPTS: 'multiple_attempts',
  MULTIPLE_FAILURES: 'multiple_failures',
  CUSTOMER_COMPLAINT: 'customer_complaint',
  TELECALLER_ESCALATION: 'telecaller_escalation',
  MANAGER_ESCALATION: 'manager_escalation',
  WAREHOUSE_ESCALATION: 'warehouse_escalation',
  CUSTOMER_QUERY: 'customer_query',
  COURIER_INFO: 'courier_info',
};

export const REASON_LABELS = {
  pickup_not_done: 'Shipment Not Picked Up', pickup_delayed: 'Pickup Delayed',
  pickup_cancelled: 'Pickup Cancelled', manifest_failure: 'Manifest Failure',
  shipment_delayed: 'Shipment Delayed Beyond SLA', ofd_exceeded: 'Out For Delivery Exceeds SLA',
  ndr: 'NDR Received', customer_unavailable: 'Customer Unavailable',
  address_incomplete: 'Address Incomplete', wrong_mobile: 'Wrong Mobile',
  pincode_issue: 'Pincode Issue', lost: 'Courier Lost Shipment', damaged: 'Shipment Damaged',
  fake_delivery: 'Fake Delivery', delivered_not_received: 'Delivered But Not Received',
  wrong_delivery: 'Wrong Product Delivered', tampered: 'Parcel Tampered',
  parcel_missing: 'Parcel Missing', partial_shipment: 'Partial Shipment',
  rto_initiated: 'RTO Initiated', rto_delivered: 'RTO Delivered',
  multiple_attempts: 'Multiple Delivery Attempts', multiple_failures: 'Multiple Courier Failures',
  customer_complaint: 'Customer Complaint', telecaller_escalation: 'Telecaller Escalation',
  manager_escalation: 'Manager Escalation', warehouse_escalation: 'Warehouse Escalation',
  customer_query: 'Customer Query', courier_info: 'Courier Information',
};

// ─── Priority classification (Critical/High/Medium/Low) ──────────────────────
const CRITICAL = new Set(['lost', 'wrong_delivery', 'fake_delivery', 'damaged', 'tampered', 'parcel_missing', 'delivered_not_received']);
const HIGH = new Set(['shipment_delayed', 'ofd_exceeded', 'multiple_attempts', 'multiple_failures', 'rto_initiated', 'rto_delivered', 'customer_complaint', 'manifest_failure', 'partial_shipment']);
const MEDIUM = new Set(['address_incomplete', 'wrong_mobile', 'pincode_issue', 'ndr', 'customer_unavailable', 'pickup_not_done', 'pickup_delayed', 'pickup_cancelled', 'telecaller_escalation', 'warehouse_escalation', 'manager_escalation']);
// everything else → low

export const PRIORITIES = ['critical', 'high', 'medium', 'low'];

// VIP always forces Critical. Otherwise classify by reason.
export const priorityForReason = (reason, { vip = false } = {}) => {
  if (vip) return 'critical';
  if (CRITICAL.has(reason)) return 'critical';
  if (HIGH.has(reason)) return 'high';
  if (MEDIUM.has(reason)) return 'medium';
  return 'low';
};

// Map a complaint issue-type string → canonical reason (best effort, keyword based).
export const reasonForIssueType = (issueType = '') => {
  const s = String(issueType).toLowerCase();
  if (s.includes('lost')) return ESCALATION_REASONS.LOST;
  if (s.includes('fake')) return ESCALATION_REASONS.FAKE_DELIVERY;
  if (s.includes('damage')) return ESCALATION_REASONS.DAMAGED;
  if (s.includes('tamper')) return ESCALATION_REASONS.TAMPERED;
  if (s.includes('wrong') && s.includes('deliver')) return ESCALATION_REASONS.WRONG_DELIVERY;
  if (s.includes('not received') || (s.includes('delivered') && s.includes('not'))) return ESCALATION_REASONS.DELIVERED_NOT_RECEIVED;
  if (s.includes('rto') || s.includes('returned to origin')) return ESCALATION_REASONS.RTO_INITIATED;
  if (s.includes('ndr')) return ESCALATION_REASONS.NDR;
  if (s.includes('not picked')) return ESCALATION_REASONS.PICKUP_NOT_DONE;
  if (s.includes('out for delivery')) return ESCALATION_REASONS.OFD_EXCEEDED;
  if (s.includes('refused') || s.includes('unavailable') || s.includes('not available')) return ESCALATION_REASONS.CUSTOMER_UNAVAILABLE;
  if (s.includes('address')) return ESCALATION_REASONS.ADDRESS_INCOMPLETE;
  if (s.includes('mobile') || s.includes('number')) return ESCALATION_REASONS.WRONG_MOBILE;
  if (s.includes('pincode') || s.includes('serviceable')) return ESCALATION_REASONS.PINCODE_ISSUE;
  if (s.includes('delay') || s.includes('pending') || s.includes('hub')) return ESCALATION_REASONS.SHIPMENT_DELAYED;
  return ESCALATION_REASONS.CUSTOMER_COMPLAINT;
};

// ─── Ownership ladder ────────────────────────────────────────────────────────
export const OWNERSHIP_LADDER = [
  'courier_executive', 'courier_team_leader', 'courier_manager',
  'operations_manager', 'vendor_escalation', 'director',
];
export const OWNERSHIP_LABELS = {
  courier_executive: 'Courier Executive', courier_team_leader: 'Courier Team Leader',
  courier_manager: 'Courier Manager', operations_manager: 'Operations Manager',
  vendor_escalation: 'Vendor Escalation', director: 'Director',
};
// Next rung up the ladder; stays at top if already at director.
export const nextOwner = (currentLevel) => {
  const i = OWNERSHIP_LADDER.indexOf(currentLevel);
  if (i < 0) return OWNERSHIP_LADDER[0];
  return OWNERSHIP_LADDER[Math.min(i + 1, OWNERSHIP_LADDER.length - 1)];
};

// ─── Workflow ────────────────────────────────────────────────────────────────
export const ESCALATION_STATUSES = [
  'new', 'assigned', 'courier_contacted', 'courier_response', 'investigation',
  'waiting_customer', 'waiting_warehouse', 'reship_approved', 'refund_approved',
  'vendor_escalation', 'resolved', 'closed',
];
export const STATUS_LABELS = {
  new: 'New', assigned: 'Assigned', courier_contacted: 'Courier Contacted',
  courier_response: 'Courier Response Received', investigation: 'Investigation',
  waiting_customer: 'Waiting on Customer', waiting_warehouse: 'Waiting on Warehouse',
  reship_approved: 'Reship Approved', refund_approved: 'Refund Approved',
  vendor_escalation: 'Vendor Escalation', resolved: 'Resolved', closed: 'Closed',
};
export const ACTIVE_STATUSES = new Set(ESCALATION_STATUSES.filter(s => s !== 'resolved' && s !== 'closed'));
export const isActiveStatus = (status) => ACTIVE_STATUSES.has(status);

// A status is reachable from any non-terminal status (managers move freely),
// except you cannot leave a closed escalation without explicitly reopening.
export const canTransition = (from, to) => {
  if (!ESCALATION_STATUSES.includes(to)) return false;
  if (from === to) return false;
  if (from === 'closed') return to === 'new' || to === 'assigned'; // reopen only
  return true;
};

// ─── SLA engine ──────────────────────────────────────────────────────────────
// Hours per priority for first response and full resolution.
export const SLA_MATRIX = {
  critical: { response: 1, resolution: 8 },
  high: { response: 4, resolution: 24 },
  medium: { response: 8, resolution: 48 },
  low: { response: 24, resolution: 72 },
};
const HOUR = 3600000;

export const computeDueTimes = (priority, createdAt = Date.now()) => {
  const m = SLA_MATRIX[priority] || SLA_MATRIX.low;
  return {
    responseDueAt: createdAt + m.response * HOUR,
    resolutionDueAt: createdAt + m.resolution * HOUR,
  };
};

// Live SLA status. Accounts for accumulated pause time (timer frozen while paused).
// Returns remaining ms (can be negative = overdue), breach + warning flags, color.
export const slaStatus = (esc = {}, now = Date.now()) => {
  if (!esc.resolutionDueAt) return { breached: false, warning: false, remainingMs: null, color: 'green', paused: false };
  const terminal = esc.status === 'resolved' || esc.status === 'closed';
  const pausedMs = Number(esc.accumulatedPauseMs || 0) + (esc.slaPausedAt ? now - esc.slaPausedAt : 0);
  const effectiveNow = now - pausedMs;
  const remainingMs = esc.resolutionDueAt - effectiveNow;
  const breached = !terminal && remainingMs < 0;
  const warning = !terminal && !breached && remainingMs <= HOUR; // within 1h
  let color = 'green';
  if (terminal) color = 'slate';
  else if (breached) color = 'red';
  else if (warning) color = 'yellow';
  return {
    breached, warning, remainingMs, paused: !!esc.slaPausedAt, color, terminal,
    responseRemainingMs: esc.responseDueAt ? esc.responseDueAt - effectiveNow : null,
  };
};

// Which warning windows have been crossed (for idempotent notifications).
// Returns 'breach' | '30m' | '1h' | null.
export const slaWarningBucket = (esc = {}, now = Date.now()) => {
  const s = slaStatus(esc, now);
  if (s.remainingMs == null || s.terminal) return null;
  if (s.remainingMs < 0) return 'breach';
  if (s.remainingMs <= HOUR / 2) return '30m';
  if (s.remainingMs <= HOUR) return '1h';
  return null;
};

// ─── Numbering ───────────────────────────────────────────────────────────────
export const formatCesNumber = (n) => `CES-${String(n || 0).padStart(6, '0')}`;
